import React, { useState, useEffect } from 'react';
import { fetchLogs, fetchLogSummary, fetchLogsBySource } from '../services/logService';
import '../styles/ThirdPartyLogs.css';

const ThirdPartyLogs = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sourceCounts, setSourceCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    hasMore: false
  });
  const [filters, setFilters] = useState({
    source: '',
    severity: '',
    startDate: '',
    endDate: ''
  });

  // Load logs on initial mount and when filters/pagination change
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Fetch logs with current filters and pagination
        const logsResponse = await fetchLogs({
          limit: pagination.limit,
          offset: pagination.offset,
          ...filters
        });
        
        setLogs(logsResponse.data);
        setPagination({
          ...pagination,
          hasMore: logsResponse.pagination?.hasMore || false
        });
        
        // Only fetch summary and source stats on initial load or time range change
        if (pagination.offset === 0) {
          const summaryResponse = await fetchLogSummary(timeRange);
          setSummary(summaryResponse.data);
          
          const sourceResponse = await fetchLogsBySource(timeRange);
          setSourceCounts(sourceResponse.data);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading log data:', err);
        setError('Failed to load log data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [pagination.limit, pagination.offset, timeRange, 
      filters.source, filters.severity, filters.startDate, filters.endDate]);

  // Handle pagination
  const handleNextPage = () => {
    if (pagination.hasMore) {
      setPagination({
        ...pagination,
        offset: pagination.offset + pagination.limit
      });
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination({
        ...pagination,
        offset: Math.max(0, pagination.offset - pagination.limit)
      });
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
    // Reset pagination when filters change
    setPagination({
      ...pagination,
      offset: 0
    });
  };

  // Handle time range changes
  const handleTimeRangeChange = (e) => {
    setTimeRange(e.target.value);
  };

  // Get severity class name
  const getSeverityClass = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return 'severity-critical';
      case 'ERROR': return 'severity-error';
      case 'WARNING': return 'severity-warning';
      case 'INFO': return 'severity-info';
      default: return '';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="third-party-logs">
      <h2>Third-Party Logs</h2>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <h3>Total Logs</h3>
            <p className="summary-value">{summary.total_logs || 0}</p>
          </div>
          <div className="summary-card severity-critical">
            <h3>Critical</h3>
            <p className="summary-value">{summary.critical_count || 0}</p>
          </div>
          <div className="summary-card severity-error">
            <h3>Errors</h3>
            <p className="summary-value">{summary.error_count || 0}</p>
          </div>
          <div className="summary-card severity-warning">
            <h3>Warnings</h3>
            <p className="summary-value">{summary.warning_count || 0}</p>
          </div>
          <div className="summary-card severity-info">
            <h3>Info</h3>
            <p className="summary-value">{summary.info_count || 0}</p>
          </div>
          <div className="summary-card">
            <h3>Sources</h3>
            <p className="summary-value">{summary.source_count || 0}</p>
          </div>
        </div>
      )}

      {/* Filters and controls */}
      <div className="controls">
        <div className="filters">
          <div className="filter-item">
            <label htmlFor="timeRange">Time Range:</label>
            <select 
              id="timeRange" 
              value={timeRange} 
              onChange={handleTimeRangeChange}
            >
              <option value="1h">Last Hour</option>
              <option value="6h">Last 6 Hours</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
          
          <div className="filter-item">
            <label htmlFor="source">Source:</label>
            <input 
              type="text" 
              id="source" 
              name="source" 
              value={filters.source} 
              onChange={handleFilterChange} 
              placeholder="Filter by source"
            />
          </div>
          
          <div className="filter-item">
            <label htmlFor="severity">Severity:</label>
            <select 
              id="severity" 
              name="severity" 
              value={filters.severity} 
              onChange={handleFilterChange}
            >
              <option value="">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="ERROR">Error</option>
              <option value="WARNING">Warning</option>
              <option value="INFO">Info</option>
            </select>
          </div>
          
          <div className="filter-item">
            <label htmlFor="startDate">From:</label>
            <input 
              type="datetime-local" 
              id="startDate" 
              name="startDate" 
              value={filters.startDate} 
              onChange={handleFilterChange}
            />
          </div>
          
          <div className="filter-item">
            <label htmlFor="endDate">To:</label>
            <input 
              type="datetime-local" 
              id="endDate" 
              name="endDate" 
              value={filters.endDate} 
              onChange={handleFilterChange}
            />
          </div>
        </div>
      </div>

      {/* Source distribution */}
      {sourceCounts?.length > 0 && (
        <div className="source-distribution">
          <h3>Log Sources</h3>
          <div className="source-bars">
            {sourceCounts.map((item, index) => (
              <div key={index} className="source-bar-container">
                <div className="source-name">{item.source}</div>
                <div className="source-bar-wrapper">
                  <div 
                    className="source-bar" 
                    style={{ 
                      width: `${Math.min(100, (item.count / sourceCounts[0].count) * 100)}%` 
                    }}
                  ></div>
                  <span className="source-count">{item.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error message */}
      {error && <div className="error-message">{error}</div>}

      {/* Loading indicator */}
      {loading && <div className="loading-spinner"></div>}

      {/* Logs table */}
      {!loading && logs?.length === 0 ? (
        <div className="no-logs">No logs found matching your criteria.</div>
      ) : (
        <div className="logs-table-container">
          <table className="logs-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Source</th>
                <th>Severity</th>
                <th>Category</th>
                <th>Message</th>
                <th>User ID</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <tr key={index} className={getSeverityClass(log.severity)}>
                  <td>{formatDate(log.timestamp)}</td>
                  <td>{log.source}</td>
                  <td>{log.severity}</td>
                  <td>{log.category}</td>
                  <td className="message-cell">{log.message}</td>
                  <td>{log.user_id || '-'}</td>
                  <td>{log.ip_address || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination controls */}
      <div className="pagination">
        <button 
          onClick={handlePrevPage} 
          disabled={pagination.offset === 0 || loading}
        >
          Previous
        </button>
        <span>
          {pagination.offset + 1}-
          {pagination.offset + logs.length} entries
        </span>
        <button 
          onClick={handleNextPage} 
          disabled={!pagination.hasMore || loading}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ThirdPartyLogs; 